import { computed, defineComponent, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { FORM_INPUT_FIELD_PROPS } from '../form/input/types'
import type { ColorPickerProps } from '../color-picker/types'

const DEFAULT_COLOR = '#2563EB'
const PRESET_COLORS = [
  '#01266C', '#0F172A', '#1D4ED8', '#2563EB', '#3B82F6', '#60A5FA',
  '#0EA5E9', '#0284C7', '#0D9488', '#10B981', '#22C55E', '#84CC16',
  '#F59E0B', '#F97316', '#EF4444', '#F43F5E', '#EC4899', '#D946EF',
  '#8B5CF6', '#6366F1', '#4F46E5', '#4338CA', '#312E81', '#111827'
]

const normaliseHex = (value: string): string => {
  if (!value) return ''
  let hex = value.trim()
  if (!hex.startsWith('#')) hex = `#${hex}`
  hex = hex.toUpperCase()
  if (/^#[0-9A-F]{3}$/.test(hex)) {
    hex = `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`
  }
  if (/^#[0-9A-F]{6}$/.test(hex)) {
    return hex
  }
  return ''
}

const ColorPickerV2 = defineComponent<ColorPickerProps>({
  setup(props) {
    const state = reactive({
      open: false
    })

    const rootRef = ref<HTMLElement>()
    const popupRef = ref<HTMLElement>()

    const currentHex = ref(normaliseHex(props.value as string) || DEFAULT_COLOR)
    const inputValue = ref(currentHex.value.replace('#', ''))

    watch(
      () => props.value,
      (value) => {
        const normalised = normaliseHex(typeof value === 'string' ? value : '')
        if (normalised) {
          currentHex.value = normalised
          inputValue.value = normalised.replace('#', '')
        } else if (!value) {
          currentHex.value = DEFAULT_COLOR
          inputValue.value = DEFAULT_COLOR.replace('#', '')
        }
      }
    )

    const emitValue = (value: string) => {
      const fieldRef = props.fieldRef
      const report = props.reportChange

      if (fieldRef && report) {
        report(fieldRef, value, !value)
      }

      if (props.dataRef) {
        props.dataRef.setValue(value)
      }
    }

    const setColor = (hex: string) => {
      const normalised = normaliseHex(hex) || DEFAULT_COLOR
      currentHex.value = normalised
      inputValue.value = normalised.replace('#', '')
      emitValue(normalised)
    }

    const handleToggle = () => {
      state.open = !state.open
    }

    const handleSwatchClick = (hex: string) => {
      setColor(hex)
      state.open = false
    }

    const handleInput = (event: Event) => {
      const target = event.target as HTMLInputElement
      const raw = target.value.replace(/[^0-9a-fA-F]/g, '').toUpperCase().slice(0, 6)
      inputValue.value = raw

      if (raw.length === 6) {
        const candidate = normaliseHex(`#${raw}`)
        if (candidate) setColor(candidate)
      }
    }

    const handleInputBlur = () => {
      const raw = inputValue.value

      if (raw.length === 6) {
        const candidate = normaliseHex(`#${raw}`)
        if (candidate) {
          setColor(candidate)
          return
        }
      }

      if (raw.length === 3) {
        const expanded = raw.split('').map((char) => char + char).join('')
        const candidate = normaliseHex(`#${expanded}`)
        if (candidate) {
          setColor(candidate)
          return
        }
      }

      inputValue.value = currentHex.value.replace('#', '')
    }

    const displayHex = computed(() => currentHex.value.replace('#', ''))

    const handleDocumentPointerDown = (event: PointerEvent) => {
      if (!state.open) return
      const rootEl = rootRef.value
      const popupEl = popupRef.value
      const target = event.target as Node | null
      if (!target) return
      if (rootEl && rootEl.contains(target)) return
      if (popupEl && popupEl.contains(target)) return
      state.open = false
    }

    onMounted(() => {
      document.addEventListener('pointerdown', handleDocumentPointerDown)
    })

    onBeforeUnmount(() => {
      document.removeEventListener('pointerdown', handleDocumentPointerDown)
    })

    return () => (
      <div ref={rootRef} class="relative inline-block w-full">
        <button
          type="button"
          class="flex h-12 w-full items-center gap-3 rounded-sm border border-gray-300 px-4 text-left transition hover:border-gray-300 focus:border-indigo-500 focus:outline-hidden"
          onClick={handleToggle}
        >
          <span
            class="h-8 w-8 rounded-sm border border-gray-200"
            style={{ backgroundColor: currentHex.value }}
          />
          <span class="text-sm font-medium text-gray-400">#</span>
          <span class="text-sm font-medium text-gray-900 tracking-wide">
            {displayHex.value}
          </span>
        </button>

        {state.open && (
          <div
            ref={popupRef}
            class="absolute z-50 w-64 rounded-lg border border-gray-200 bg-white p-4 shadow-lg"
            style={{ left: '0', right: 'auto', top: 'auto', bottom: 'calc(100% + 0.5rem)' }}
          >
            <div class="mb-3 text-xs font-medium uppercase tracking-wide text-gray-400">
              Presets
            </div>
            <div class="mb-4 grid grid-cols-4 gap-2">
              {PRESET_COLORS.map((hex) => (
                <button
                  key={hex}
                  type="button"
                  class={[
                    'h-8 w-8 rounded-sm border transition focus:outline-hidden focus-visible:ring-2 focus-visible:ring-indigo-500',
                    currentHex.value === hex ? 'border-indigo-500 ring-2 ring-indigo-500' : 'border-gray-200'
                  ]}
                  style={{ backgroundColor: hex }}
                  onClick={() => handleSwatchClick(hex)}
                />
              ))}
            </div>
            <div class="flex items-center gap-2">
              <span class="text-sm font-medium text-gray-400">#</span>
              <input
                type="text"
                value={inputValue.value}
                onInput={handleInput}
                onBlur={handleInputBlur}
                class="flex-1 rounded-sm border border-gray-200 px-3 py-2 text-sm uppercase tracking-wide text-gray-900 focus:border-indigo-500 focus:outline-hidden"
                placeholder="FFFFFF"
                maxlength="6"
              />
            </div>
          </div>
        )}
      </div>
    )
  }
})

ColorPickerV2.props = FORM_INPUT_FIELD_PROPS

export default ColorPickerV2
